import { ModalsProvider as MantineModalsProvider, openConfirmModal as mantineOpenConfirmModal } from "@mantine/modals";
import type * as React from "react";

/**
 * The owned imperative confirm. The census over all 12 `modals.openConfirmModal` call sites
 * (SC-2700) found exactly this shape; the vendor's wider `ModalSettings` surface (contextual
 * modals, `openModal`, `closeAllModals`) has zero consumers and deliberately does not exist
 * here. The overlap with `ConfirmationModal` (type-to-confirm delete UX) is a per-site UX
 * decision recorded on the PR — landing sites there would change behavior, so it belongs to
 * SC-2698, not this migration.
 */
export interface ConfirmModalOptions {
  title?: React.ReactNode;
  children?: React.ReactNode;
  labels?: { confirm: React.ReactNode; cancel: React.ReactNode };
  /** Confirm-button styling — the census found only `color` and `leftIcon` in use. `autoFocus: false` focuses Cancel. */
  confirmProps?: { color?: string; leftIcon?: React.ReactNode; autoFocus?: boolean };
  centered?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
}

export const openConfirmModal = (options: ConfirmModalOptions): void => {
  // Mantine 7 renamed the Button's `leftIcon` to `leftSection`; the owned key is unchanged.
  const { leftIcon, ...confirmButtonProps }: NonNullable<ConfirmModalOptions["confirmProps"]> = options.confirmProps ?? {};
  const confirmProps = {
    ...confirmButtonProps,
    ...(leftIcon === undefined ? {} : { leftSection: leftIcon }),
    "data-sc-print-hidden": "true",
  };
  // ConfirmModal falls back to this same label when `cancelProps.children` is absent.
  const cancelProps = {
    children: options.labels?.cancel,
    "data-sc-print-hidden": "true",
    ...(options.confirmProps?.autoFocus === false ? { autoFocus: true } : {}),
  };

  mantineOpenConfirmModal({ ...options, cancelProps, confirmProps });
};

/**
 * Mantine 6's Modal animated its content with `pop` over 200ms (`Modal.js` defaults); Mantine 7's defaults
 * to `fade-down`. The provider spreads its `modalProps` under each confirm modal's own props, so the host
 * restates the Mantine 6 transition there (SC-2703), as the owned `Modal` does in `overlays.tsx`.
 */
const CONFIRM_MODAL_TRANSITION: { transition: "pop"; duration: number } = { transition: "pop", duration: 200 };
const HOST_MODAL_PROPS = { transitionProps: CONFIRM_MODAL_TRANSITION };

/**
 * The provider `ShelfCycleProvider` mounts; not exported from the barrel. The owned annotation
 * keeps the vendor's provider props type out of the emitted declarations (`lint:ui-types` gate) —
 * the host only ever wraps children.
 */
export const ModalsHost: React.ComponentType<{ children: React.ReactNode }> = ({ children }) => (
  <MantineModalsProvider modalProps={HOST_MODAL_PROPS}>{children}</MantineModalsProvider>
);
